import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, ScanFace } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SLOTS = [["identita_fronte", "Documento d'identità – fronte", "Carta d'identità, passaporto o patente: lato con la foto"],
  ["identita_retro", "Documento d'identità – retro", "Lo stesso documento: lato posteriore"],
  ["tessera_ordine", "Tessera dell'ordine – fronte", "Fronte della tessera di iscrizione all'ordine professionale", true],
  ["albo", "Iscrizione all'albo", "Certificato di iscrizione all'albo/ordine o, se non previsto, attestato di qualifica"]];

export function OperatorDocs({ v, reload }) {
  const [busy, setBusy] = useState("");
  const editable = ["PENDING", "UNDER_REVIEW"].includes(v?.status);
  const upload = async (type, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", type);
    setBusy(type);
    try {
      await api.post("/professional/verification/document", fd);
      toast.success("Documento caricato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-2" data-testid="operator-docs">
      {SLOTS.map(([k, l, hint, ordine]) => {
        const docs = (v?.documents || []).filter((d) => d.doc_type === k || (d.doc_type === "identita" && k.startsWith("identita_")));
        const required = !ordine || v?.credential?.albo_registered;
        return (
          <div key={k} className={`rounded-xl border p-3 ${docs.length ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`operator-doc-slot-${k}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">{l} {required ? <span className="text-rose-500">*</span> : <span className="text-[11px] font-normal text-slate-400">(se iscritto a un ordine)</span>}</p>
              {docs.length > 0 && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`operator-doc-ok-${k}`}><CheckCircle2 className="h-3.5 w-3.5" />{docs[docs.length - 1].filename}</span>}
            </div>
            <p className="text-[11px] text-slate-500">{hint} · PDF, JPG o PNG, max 10 MB</p>
            {editable && (
              <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-rose-300">
                {busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}{docs.length ? "Sostituisci" : "Carica file"}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid={`operator-doc-input-${k}`} onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function IdentityStep() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const didit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/identity/didit/session");
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="identity-step">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-rose-500">Passo 4 · Verifica identità</p>
      <button type="button" data-testid="identity-didit-btn" disabled={busy} onClick={didit} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanFace className="h-4 w-4" />}Verifica con Didit (documento + selfie)</button>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid="identity-spid-btn" onClick={() => navigate("/profilo?tab=identita")} className="rounded-xl bg-[#0066CC] px-3 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#004c99]">SPID <span className="font-normal opacity-80">(sandbox)</span></button>
        <button type="button" data-testid="identity-cie-btn" onClick={() => navigate("/profilo?tab=identita")} className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-xs font-bold text-slate-800 transition-colors hover:border-slate-400">CIE <span className="font-normal text-slate-500">(sandbox)</span></button>
      </div>
      <p className="text-[11px] text-slate-500">SPID e CIE sono in ambiente di test e non ancora operativi.</p>
    </div>
  );
}

export function OperatorDetails({ usage }) {
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [f, setF] = useState({ codice_fiscale: "", degree: "", albo_number: "", order_name: "" });
  const load = () => api.get("/professional/verification").then(({ data }) => setV(data));
  useEffect(() => {
    load();
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data: p } = await api.get("/profile");
      await api.post("/professional/verification", { ...f, profession: usage, first_name: p.first_name, last_name: p.last_name, albo_registered: !!f.albo_number });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const complete = (v?.missing_docs || ["x"]).length === 0;
  if (v === null) return null;
  return (
    <div className="space-y-4" data-testid="operator-details">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-rose-500">Passo 3 · Dati professionali e documenti</p>
      {!v.id ? (
        <form onSubmit={submit} className="space-y-3" data-testid="operator-details-form">
          <div className="space-y-1"><Label>Codice fiscale</Label><Input data-testid="operator-cf-input" required minLength={16} maxLength={16} value={f.codice_fiscale} onChange={(e) => setF({ ...f, codice_fiscale: e.target.value.toUpperCase() })} /></div>
          <div className="space-y-1"><Label>Titolo di studio / qualifica</Label><Input data-testid="operator-degree-input" required value={f.degree} onChange={set("degree")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>N. iscrizione albo</Label><Input data-testid="operator-albo-input" value={f.albo_number} onChange={set("albo_number")} placeholder="se previsto" /></div>
            <div className="space-y-1"><Label>Ordine / ente</Label><Input data-testid="operator-order-input" value={f.order_name} onChange={set("order_name")} placeholder="es. OMCeO Roma" /></div>
          </div>
          <Button data-testid="operator-details-submit" className="w-full rounded-full bg-rose-600 hover:bg-rose-700">Continua con i documenti</Button>
        </form>
      ) : (
        <>
          <OperatorDocs v={v} reload={load} />
          {complete
            ? <div className="space-y-3" data-testid="operator-complete"><p className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-900">Documenti ricevuti. Ultimo passo: <b>verifica la tua identità</b>. Dopo la verifica il profilo passa all'approvazione dell'amministratore.</p><IdentityStep /><Button variant="outline" data-testid="operator-finish-btn" onClick={() => navigate("/")} className="w-full rounded-full">Lo farò più tardi</Button></div>
            : <p className="text-xs text-slate-500">Carica tutti i documenti obbligatori (*) per inviare la richiesta di approvazione.</p>}
        </>
      )}
    </div>
  );
}
